import { useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';

type Role = 'ADMIN' | 'MANAGER' | 'USER';

const roleHierarchy: Record<Role, number> = {
  USER: 1,
  MANAGER: 2,
  ADMIN: 3,
};

export function usePermissions() {
  const user = useAuthStore((s) => s.user);

  const hasRole = useCallback(
    (role: Role): boolean => {
      if (!user) return false;
      return roleHierarchy[user.role as Role] >= roleHierarchy[role];
    },
    [user],
  );

  const hasPermission = useCallback(
    (resource: string, action: string): boolean => {
      if (!user) return false;
      const role = user.role as Role;

      // Admin has full access
      if (role === 'ADMIN') return true;

      // Resource-specific permissions
      const permissions: Record<string, Record<string, Role[]>> = {
        users: { read: ['MANAGER'], create: [], update: [], delete: [] },
        leads: { read: ['USER', 'MANAGER'], create: ['USER', 'MANAGER'], update: ['USER', 'MANAGER'], delete: ['MANAGER'] },
        contacts: { read: ['USER', 'MANAGER'], create: ['USER', 'MANAGER'], update: ['USER', 'MANAGER'], delete: ['MANAGER'] },
        companies: { read: ['USER', 'MANAGER'], create: ['USER', 'MANAGER'], update: ['USER', 'MANAGER'], delete: ['MANAGER'] },
        deals: { read: ['USER', 'MANAGER'], create: ['USER', 'MANAGER'], update: ['USER', 'MANAGER'], delete: ['MANAGER'] },
        pipelines: { read: ['USER', 'MANAGER'], create: [], update: [], delete: [] },
        tasks: { read: ['USER', 'MANAGER'], create: ['USER', 'MANAGER'], update: ['USER', 'MANAGER'], delete: ['USER', 'MANAGER'] },
        expenses: { read: ['USER', 'MANAGER'], create: ['USER', 'MANAGER'], update: ['USER', 'MANAGER'], delete: ['USER'] },
        invoices: { read: ['USER', 'MANAGER'], create: ['MANAGER'], update: ['MANAGER'], delete: [] },
        payments: { read: ['MANAGER'], create: ['MANAGER'], update: [], delete: [] },
        accounts: { read: ['MANAGER'], create: [], update: [], delete: [] },
        transactions: { read: ['MANAGER'], create: ['MANAGER'], update: [], delete: [] },
        budgets: { read: ['MANAGER'], create: [], update: [], delete: [] },
        reports: { read: ['MANAGER'], create: [], update: [], delete: [] },
      };

      const resourcePerms = permissions[resource];
      if (!resourcePerms) return false;

      const allowedRoles = resourcePerms[action];
      if (!allowedRoles) return false;

      return allowedRoles.includes(role);
    },
    [user],
  );

  const canAccess = useCallback(
    (path: string): boolean => {
      if (!user) return false;
      const role = user.role as Role;

      // Admin paths
      if (path.startsWith('/settings/team') || path.startsWith('/settings/pipelines')) {
        return role === 'ADMIN';
      }

      // Financial paths
      if (path.startsWith('/financial/accounts') || path.startsWith('/financial/budgets')) {
        return role === 'ADMIN' || role === 'MANAGER';
      }

      return true;
    },
    [user],
  );

  return { hasRole, hasPermission, canAccess, userRole: user?.role as Role | undefined };
}
